import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface ProductObject {
  nameEn: string;
  nameAe: string;
  phone: string;
  latitude: string;
  longitude: string;
  id: string;
}

export interface ProductListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  product: Array<ProductObject>;
  errors: unknown;
}

export interface ProductFilterInfo {
  name: string;
}

@Module
export default class ProductModule
  extends VuexModule
  implements ProductListInfo
{
  product = [] as Array<ProductObject>;
  allProduct = [] as Array<ProductObject>;
  productFilter = {} as ProductFilterInfo;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get product list
   * @returns Array<ProductObject>
   */
  get productList(): Array<ProductObject> {
    return this.product;
  }

  get allProductList(): Array<ProductObject> {
    return this.allProduct;
  }

  /**
   * Get total items
   * @returns number
   */
  get productListCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_PRODUCTS](data) {
    this.product = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_PRODUCTS](data) {
    this.allProduct = data.data;
  }

  @Mutation
  [Mutations.SET_PRODUCT_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_PRODUCT_LIST]() {
    this.product = [] as Array<ProductObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_PRODUCT_FILTER](obj) {
    this.productFilter.name = obj.name;
  }

  @Action
  [Actions.RESET_PRODUCT_STORE]() {
    this.context.commit(Mutations.RESET_PRODUCT_LIST);
  }

  @Action
  [Actions.UPDATE_PRODUCT_FILTER](val) {
    this.context.commit(Mutations.SET_PRODUCT_FILTER, val);
  }

  @Action
  [Actions.CREATE_PRODUCT](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/product', data)
      .then((data) => {
        this.context.commit(Mutations.RESET_PRODUCT_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_PRODUCT_ATTACHMENT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/product/attachments/${id}`, data)
      .then(() => {
        this.context.commit(Mutations.RESET_GROOMERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_PRODUCT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/product', id)
      .then(() => {
        this.context.commit(Mutations.RESET_PRODUCT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_PRODUCT_ATTACHMENT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/product/attachments', id)
      .then(() => {
        this.context.commit(Mutations.RESET_PRODUCT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }
  @Action
  [Actions.UPDATE_PRODUCT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/product', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_PRODUCT_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_PRODUCT_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_PRODUCT_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_PRODUCT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/product', id);
  }

  @Action
  [Actions.GET_PRODUCTS]() {
    const params = {
      page: this.page,
      name: this.productFilter.name,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/product', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_PRODUCTS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_ALL_PRODUCT]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get(`/product/all`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_PRODUCTS, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
